import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import DotsLoading from 'components/loadings/dotsLoading'
import SortPill from 'components/sortPill'
import Item from './item'
import InfiniteScroll from 'react-infinite-scroll-component'
import {
    FetchNextPageOptions,
    InfiniteData,
    InfiniteQueryObserverResult,
} from 'react-query'
import { IoSearch, IoClose, IoOptionsOutline } from 'react-icons/io5'
import { FaArrowRight } from 'react-icons/fa6'
import { TFundLoanSort, TFundLoanStatus } from 'services/fund'
import { useEntryState } from 'utilities/navigation/entryState'

/**
 * A manager's «ترتیب» on this tab. The server orders the WHOLE list, not the
 * page on screen (LoanView.MANAGER_SORTS).
 *
 * The default used to be fixed: the manager's OWN open loans pinned to the
 * top, then overdue, then open — so a manager's healthy loan sat above a
 * member's overdue one, which read as a broken «معوق first» order. Android
 * still gets that order; this tab now asks for one by name.
 */
export const LOAN_SORTS: { key: TFundLoanSort; label: string }[] = [
    { key: 'oldest_overdue', label: 'قدیمی‌ترین قسط معوق' },
    { key: 'most_overdue', label: 'بیشترین مبلغ معوق' },
    { key: 'most_remaining', label: 'بیشترین مانده وام' },
    { key: 'newest', label: 'جدیدترین وام' },
    { key: 'oldest', label: 'قدیمی‌ترین وام' },
]

export const DEFAULT_LOAN_SORT: TFundLoanSort = 'oldest_overdue'

/**
 * «معوق» is what the card's red badge means; «جاری» is still being repaid with
 * nothing overdue; «تسویه‌شده» has nothing left to pay. The three don't
 * overlap, so together they are «همه».
 */
const LOAN_STATUSES: {
    key: TFundLoanStatus
    label: string
    aria: string
    empty: string
}[] = [
    { key: '', label: 'همه', aria: 'همهٔ وام‌ها', empty: '' },
    {
        key: 'overdue',
        label: 'معوق',
        aria: 'فقط وام‌های معوق',
        empty: 'هیچ وامی قسط معوق ندارد.',
    },
    {
        key: 'active',
        label: 'جاری',
        aria: 'فقط وام‌های جاری',
        empty: 'وامی در حال بازپرداخت و بدون معوقه نیست.',
    },
    {
        key: 'settled',
        label: 'تسویه‌شده',
        aria: 'فقط وام‌های تسویه‌شده',
        empty: 'هنوز وامی تسویه نشده است.',
    },
]

interface ILoansProps {
    data: InfiniteData<IMoneyPoolFundLoans> | undefined
    isLoading: boolean
    fetchNextPage: (
        options?: FetchNextPageOptions | undefined
    ) => Promise<InfiniteQueryObserverResult<IMoneyPoolFundLoans, unknown>>
    isError: boolean
    setSearchLoans: React.Dispatch<React.SetStateAction<string>>
    searchLoan: string
    setSearchLoan: React.Dispatch<React.SetStateAction<string>>
    isManager?: boolean
    isHiddenLoans?: boolean
    sort?: TFundLoanSort
    onSortChange?: (next: TFundLoanSort) => void
    status?: TFundLoanStatus
    onStatusChange?: (next: TFundLoanStatus) => void
    /**
     * Every tab's list stays mounted and each listens to the same window
     * scroll; only the one on screen should answer it by paging.
     */
    isActive?: boolean
}
export default function Loans({
    data,
    isLoading,
    fetchNextPage,
    isError,
    setSearchLoans,
    searchLoan,
    setSearchLoan,
    isManager,
    isHiddenLoans,
    sort = DEFAULT_LOAN_SORT,
    onSortChange,
    status = '',
    onStatusChange,
    isActive = true,
}: ILoansProps) {
    const { id } = useParams()
    // kept with the search itself, so a search restored on back is on screen
    const [searchMode, setSearchMode] = useEntryState(
        'fund.loanSearchOpen',
        false
    )
    const [panelOpen, setPanelOpen] = useState(false)

    const togglePanel = () => {
        if (!panelOpen) {
            posthog.capture('fund_loans_sort_opened', {
                moneypool_id: Number(id),
                sort,
                status: status || null,
            })
        }
        setPanelOpen(!panelOpen)
    }

    const chooseSort = (next: TFundLoanSort) => {
        if (next === sort) return
        onSortChange?.(next)
        posthog.capture('fund_loans_sorted', {
            moneypool_id: Number(id),
            sort: next,
            status: status || null,
        })
    }

    const chooseStatus = (next: TFundLoanStatus) => {
        if (next === status) return
        onStatusChange?.(next)
        posthog.capture('fund_loans_status_filtered', {
            moneypool_id: Number(id),
            status: next || null,
            sort,
        })
    }

    const sortLabel = LOAN_SORTS.find((option) => option.key === sort)?.label
    const statusLabel = LOAN_STATUSES.find((option) => option.key === status)

    const closeSearch = () => {
        setSearchMode(false)
        setSearchLoan('')
    }

    return (
        <div className="bg-white">
            {isManager &&
                (searchMode ? (
                    <div className="flex w-full items-center bg-white">
                        <button
                            type="button"
                            onClick={closeSearch}
                            className="ml-3 shrink-0 p-1 text-green-1"
                        >
                            <FaArrowRight className="h-4 w-4" />
                        </button>
                        <input
                            type="text"
                            autoFocus
                            className="w-full bg-white p-4 text-right text-[15px] text-black outline-0 placeholder:text-text-muted"
                            onChange={(e) => setSearchLoan(e.target.value)}
                            value={searchLoan}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                    setSearchLoans(searchLoan)
                            }}
                            placeholder="جستجوی نام و شماره تلفن"
                        />
                        <button
                            type="button"
                            onClick={closeSearch}
                            className="mr-3 shrink-0 p-1 text-black"
                        >
                            <IoClose className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <div className="pl-4 pr-4 pt-4 pb-2">
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => setSearchMode(true)}
                                className="p-[5px] text-green-1"
                                aria-label="جستجوی وام‌ها"
                            >
                                <IoSearch className="h-[22px] w-[22px]" />
                            </button>
                            <button
                                type="button"
                                onClick={togglePanel}
                                aria-expanded={panelOpen}
                                aria-controls="fund-loans-sort"
                                className="flex items-center gap-1 text-green-1"
                            >
                                <span className="text-base font-bold text-green-1">
                                    ترتیب و فیلتر
                                </span>
                                <IoOptionsOutline className="h-[22px] w-[22px] p-[1px]" />
                            </button>
                        </div>
                        {/* What the list is ordered by stays on screen once
                            the panel is closed — otherwise a list that
                            opens in a different order than yesterday reads
                            as broken. */}
                        {!panelOpen && (
                            <p className="mt-1 text-left text-[11px] text-text-secondary">
                                به ترتیب {sortLabel}
                                {status && statusLabel
                                    ? `، فقط ${statusLabel.label}`
                                    : ''}
                            </p>
                        )}
                        {panelOpen && (
                            <div
                                id="fund-loans-sort"
                                className="mt-2 rounded-2xl border border-stroke bg-white p-3"
                            >
                                <p className="mb-1.5 text-[11px] text-text-muted">
                                    ترتیب
                                </p>
                                <div
                                    role="radiogroup"
                                    aria-label="ترتیب وام‌ها"
                                    className="flex flex-wrap items-center gap-2"
                                >
                                    {LOAN_SORTS.map((option) => (
                                        <SortPill
                                            key={option.key}
                                            checked={sort === option.key}
                                            onChange={() =>
                                                chooseSort(option.key)
                                            }
                                            label={option.label}
                                        />
                                    ))}
                                </div>
                                <p className="mb-1.5 mt-3 text-[11px] text-text-muted">
                                    وضعیت
                                </p>
                                <div
                                    role="radiogroup"
                                    aria-label="وضعیت وام‌ها"
                                    className="flex flex-wrap items-center gap-2"
                                >
                                    {LOAN_STATUSES.map((option) => (
                                        <SortPill
                                            key={option.key || 'all'}
                                            checked={status === option.key}
                                            onChange={() =>
                                                chooseStatus(option.key)
                                            }
                                            label={option.label}
                                            aria={option.aria}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            {!isLoading && data ? (
                <InfiniteScroll
                    next={fetchNextPage}
                    hasMore={
                        isActive && !!data?.pages[data.pages.length - 1].next
                    }
                    dataLength={
                        data ? data.pages.flatMap((x) => x.results).length : 0
                    }
                    loader={<DotsLoading />}
                >
                    {isHiddenLoans && !isManager && (
                        <p className="mt-2 text-center text-sm text-black">
                            فقط وام‌های خودتان را می‌بینید
                        </p>
                    )}
                    <div className="pt-2">
                        {data.pages.flatMap((x) => x.results).length === 0 &&
                            status && (
                                <p className="my-8 text-center text-sm text-text-secondary">
                                    {statusLabel?.empty}
                                </p>
                            )}
                        {data.pages
                            .flatMap((x) => x.results)
                            .map((item) => (
                                <Item item={item} key={item.id} />
                            ))}
                    </div>
                </InfiniteScroll>
            ) : (
                <>
                    {isError ? (
                        <p className="my-8 text-center text-sm text-text-secondary">
                            وامی وجود ندارد
                        </p>
                    ) : (
                        <DotsLoading />
                    )}
                </>
            )}
        </div>
    )
}
